import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Screen } from '@/components/ui';
import { ReportSentNotice } from '@/components/report-sent-notice';
import { MessageType, type Dictionary, type SavedChatDto } from '@anon-chat/shared';
import { nickname, useTranslations } from '@/shared/i18n';
import { useChatStore } from '@/store/chat.store';
import { useSessionStore } from '@/store/session.store';
import { WriteAccessNotice } from '@/components/write-access-notice';

/**
 * Строка под прозвищем: последняя реплика или чем она была.
 *
 * У фотографии и голосового текста нет, и раньше список говорил «пока ни
 * одного сообщения» — то есть неправду ровно о тех диалогах, где как раз
 * обменялись снимками.
 */
function lastLine(saved: SavedChatDto, dictionary: Dictionary): string {
  if (saved.lastMessage) {
    return saved.lastMessage;
  }

  switch (saved.lastMessageType) {
    case MessageType.PHOTO:
      return dictionary.saved.lastPhoto;
    case MessageType.VOICE:
      return dictionary.saved.lastVoice;
    default:
      return dictionary.saved.noMessages;
  }
}

/**
 * Сохранённые диалоги.
 *
 * Собеседник опознаётся тем же анонимным идентификатором, что и в самом
 * разговоре, — прозвище у него остаётся прежним. Ничего сверх прозвища о
 * человеке здесь не появляется, как и в остальном приложении.
 */
export function SavedChatsScreen(): React.ReactNode {
  const dictionary = useTranslations();
  const savedChats = useChatStore((state) => state.savedChats);
  const loadSavedChats = useChatStore((state) => state.loadSavedChats);
  const openSavedChat = useChatStore((state) => state.openSavedChat);
  const goToOnboarding = useChatStore((state) => state.goToOnboarding);
  const notificationsEnabled = useSessionStore((state) => state.user?.notificationsEnabled ?? false);
  const toggleNotifications = useSessionStore((state) => state.toggleNotifications);

  useEffect(() => {
    void loadSavedChats();
  }, [loadSavedChats]);

  const notificationsLabel = notificationsEnabled
    ? dictionary.saved.notificationsOn
    : dictionary.saved.notificationsOff;

  return (
    <Screen>
      <ReportSentNotice />

      <header className="flex items-center gap-2 border-b border-border px-3 py-2">
        <button
          type="button"
          aria-label={dictionary.common.back}
          className="flex size-9 items-center justify-center rounded-full text-text-muted active:bg-surface"
          onClick={goToOnboarding}
        >
          <svg viewBox="0 0 24 24" className="size-6" aria-hidden>
            <path
              d="M15 5l-7 7 7 7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <p className="text-[16px] font-medium text-text">{dictionary.saved.title}</p>

        {/*
          Переключатель напоминаний живёт здесь, а не на отдельном экране
          настроек: напоминания бывают только у сохранённых диалогов, и искать
          их в другом месте человеку неоткуда.
        */}
        <button
          type="button"
          aria-label={notificationsLabel}
          aria-pressed={notificationsEnabled}
          title={dictionary.saved.notificationsHint}
          className={`ml-auto flex size-9 items-center justify-center rounded-full active:bg-surface ${
            notificationsEnabled ? 'text-text' : 'text-text-muted'
          }`}
          onClick={() => void toggleNotifications()}
        >
          <svg
            viewBox="0 0 24 24"
            className="size-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6" />
            <path d="M10.3 20a2 2 0 0 0 3.4 0" />
            {!notificationsEnabled && <path d="M3 3l18 18" />}
          </svg>
        </button>
      </header>

      <WriteAccessNotice />

      <div className="flex-1 overflow-y-auto scrollbar-hidden">
        {savedChats.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-8 text-center">
            <p className="text-[15px] text-text">{dictionary.saved.emptyTitle}</p>
            <p className="text-[13px] leading-relaxed text-text-muted">
              {dictionary.saved.emptyText}
            </p>
          </div>
        ) : (
          savedChats.map((saved) => (
            <motion.button
              key={saved.chatId}
              type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={() => void openSavedChat(saved.chatId)}
              className="flex w-full items-center gap-3 border-b border-border px-3 py-3 text-left active:bg-surface"
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent/15 text-[15px] text-accent">
                {nickname(saved.partner.anonymousId).slice(0, 1)}
              </span>

              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] text-text">
                  {nickname(saved.partner.anonymousId)}
                </span>
                <span className="block truncate text-[13px] text-text-muted">
                  {lastLine(saved, dictionary)}
                </span>
              </span>

              {saved.unreadCount > 0 ? (
                <span className="flex min-w-5 shrink-0 items-center justify-center rounded-full bg-danger px-1.5 text-[11px] font-medium text-white">
                  {saved.unreadCount}
                </span>
              ) : null}
            </motion.button>
          ))
        )}
      </div>
    </Screen>
  );
}
